/**
 * Transparency route - Luật AI 2026 Sec.10 mandate
 *
 * Renders the model card from /api/v1/public/transparency. Falls back to
 * a minimal fallback message if the endpoint is down.
 *
 * BUG-2026-05-05-02: heading + section labels render single-locale via
 * useLang(); limitations list uses limitations_vi or limitations_en.
 */

import { useEffect, useState } from 'react';
import type { ModelCard } from '@core/landings-helpers';
import { FALLBACK_CARD } from '@core/landings-helpers';
import { useLang } from '../use-lang';

export function TransparencyRoute() {
  const [card, setCard] = useState<ModelCard | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { lang } = useLang();

  useEffect(() => {
    let cancelled = false;
    fetch('/api/v1/public/transparency')
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`${r.status}`))))
      .then((j) => {
        if (!cancelled) setCard(j as ModelCard);
      })
      .catch((e: Error) => {
        if (!cancelled) {
          setError(e.message);
          setCard(FALLBACK_CARD);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const t = {
    pageTitle: lang === 'vi' ? 'Asklaw - Trang minh bạch' : 'Asklaw - Transparency',
    loading: lang === 'vi' ? 'Đang tải model card...' : 'Loading model card...',
    updated: lang === 'vi' ? 'Cập nhật' : 'Updated',
    fallback: lang === 'vi' ? 'fallback' : 'fallback',
    model: lang === 'vi' ? 'Mô hình' : 'Model',
    drafter: lang === 'vi' ? 'Drafter' : 'Drafter',
    verifier: lang === 'vi' ? 'Verifier' : 'Verifier',
    embedding: lang === 'vi' ? 'Embedding' : 'Embedding',
    reranker: lang === 'vi' ? 'Reranker' : 'Reranker',
    riskClass: lang === 'vi' ? 'Phân loại rủi ro tự đánh giá' : 'Self-assessed risk class',
    fallbackKw: lang === 'vi' ? 'dự phòng' : 'fallback',
    corpus: lang === 'vi' ? 'Kho dữ liệu' : 'Corpus',
    source: lang === 'vi' ? 'Nguồn' : 'Source',
    documents: lang === 'vi' ? 'Số văn bản' : 'Documents',
    indexedChunks: lang === 'vi' ? 'Chunks đã index' : 'Indexed chunks',
    lastSync: lang === 'vi' ? 'Lần sync eff_status gần nhất' : 'Last eff_status sync',
    metrics: lang === 'vi' ? 'Chỉ số' : 'Metrics',
    citation: lang === 'vi' ? 'Độ chính xác citation' : 'Citation correctness',
    halluc: lang === 'vi' ? 'Tỉ lệ ảo giác' : 'Hallucination rate',
    refusal: lang === 'vi' ? 'Độ phủ refusal' : 'Refusal coverage',
    recall: lang === 'vi' ? 'Recall@5' : 'Retrieval recall@5',
    compliance: lang === 'vi' ? 'Tuân thủ' : 'Compliance',
    aiLabel: lang === 'vi' ? 'Nhãn AI' : 'AI label',
    auditRetention: lang === 'vi'
      ? 'Lưu audit log'
      : 'Audit log retention',
    months: lang === 'vi' ? 'tháng' : 'months',
    aiAct: lang === 'vi' ? 'Luật AI 2026 Sec.10' : 'AI Act 2026 Sec.10',
    dpo: lang === 'vi' ? 'Liên hệ DPO' : 'DPO contact',
    limitations: lang === 'vi' ? 'Giới hạn' : 'Limitations',
  };

  if (!card) {
    return (
      <main>
        <p>{t.loading}</p>
      </main>
    );
  }

  const limitations = lang === 'vi' ? card.limitations_vi : card.limitations_en;

  return (
    <main>
      <h1>{t.pageTitle}</h1>
      <p style={{ color: 'var(--color-muted)' }}>
        {t.updated}: {new Date(card.generated_at).toLocaleString(lang === 'vi' ? 'vi-VN' : 'en-US')}
        {error ? ` (${t.fallbackKw} - ${error})` : ''}
      </p>

      <h2>{t.model}</h2>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <tbody>
          <Row
            label={t.drafter}
            value={`${card.drafter.primary} (${t.fallbackKw} ${card.drafter.fallback})`}
          />
          <Row label={t.verifier} value={card.verifier} />
          <Row
            label={t.embedding}
            value={`${card.embedding.model} (${card.embedding.dimensions}d)`}
          />
          <Row label={t.reranker} value={card.reranker} />
          <Row label={t.riskClass} value={card.self_assessed_risk_class} />
        </tbody>
      </table>

      <h2>{t.corpus}</h2>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <tbody>
          <Row label={t.source} value={card.corpus.source} />
          <Row label={t.documents} value={String(card.corpus.doc_count)} />
          <Row label={t.indexedChunks} value={String(card.corpus.indexed_chunk_count)} />
          <Row label={t.lastSync} value={card.corpus.last_eff_status_sync} />
        </tbody>
      </table>

      <h2>{t.metrics}</h2>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <tbody>
          <Row label={t.citation} value={`${card.metrics.citation_correctness_pct}%`} />
          <Row label={t.halluc} value={`${card.metrics.hallucination_pct}%`} />
          <Row label={t.refusal} value={`${card.metrics.refusal_coverage_pct}%`} />
          <Row label={t.recall} value={`${card.metrics.retrieval_recall_at_5}%`} />
        </tbody>
      </table>

      <h2>{t.compliance}</h2>
      <ul>
        <li>
          {t.aiLabel}: {card.ai_label}
        </li>
        <li>
          {t.auditRetention}: {card.audit_log_retention_months} {t.months} ({t.aiAct})
        </li>
        <li>
          {t.dpo}: <a href={`mailto:${card.dpo_email}`}>{card.dpo_email}</a>
        </li>
      </ul>

      <h2>{t.limitations}</h2>
      <ul>
        {limitations.map((l, i) => (
          <li key={`${lang}-${i}`}>{l}</li>
        ))}
      </ul>
    </main>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <tr>
      <th
        scope="row"
        style={{
          textAlign: 'left',
          padding: '8px 12px',
          borderBottom: '1px solid var(--color-border)',
          width: '32%',
        }}
      >
        {label}
      </th>
      <td style={{ padding: '8px 12px', borderBottom: '1px solid var(--color-border)' }}>
        {value}
      </td>
    </tr>
  );
}
